import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { StarRating } from '../components/StarRating';
import { FeaturedProperties } from '../components/PropertySection';
import {
    MapPin,
    Phone,
    Mail,
    Award,
    Calendar as CalendarIcon,
    Clock,
    Star,
    ArrowLeft,
    CheckCircle,
    Loader2,
    User,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Helmet } from 'react-helmet-async';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgentProfilePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, token } = useAuth();

    const [agent, setAgent] = useState(null);
    const [availabilities, setAvailabilities] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [bookingNotes, setBookingNotes] = useState('');
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [bookingLoading, setBookingLoading] = useState(false);

    useEffect(() => {
        fetchAgent();
        fetchReviews();
    }, [id]);

    // Inject JSON-LD Schema.org RealEstateAgent into <head> for SEO (separate from Helmet to avoid script-children invariant)
    useEffect(() => {
        if (!agent) return;
        const fullName = `${agent.first_name || ''} ${agent.last_name || ''}`.trim();
        const description = (agent.bio?.trim()?.slice(0, 160)) || `Agent immobilier${agent.agency_name ? ` chez ${agent.agency_name}` : ''}${agent.city ? ` à ${agent.city}` : ''}. Prenez rendez-vous en ligne sur Scalimmo.`;
        const profilePic = agent.photo_url
            ? (agent.photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${agent.photo_url}` : agent.photo_url)
            : `${process.env.REACT_APP_BACKEND_URL}/icons/icon-512.png`;
        const profileUrl = `${window.location.origin}/agents/${id}`;

        const jsonLd = {
            '@context': 'https://schema.org',
            '@type': 'RealEstateAgent',
            name: fullName,
            url: profileUrl,
            image: profilePic,
            description,
            telephone: agent.phone || '',
            email: agent.email || '',
            worksFor: { '@type': 'Organization', name: agent.agency_name || 'Indépendant' },
            address: {
                '@type': 'PostalAddress',
                addressLocality: agent.city || '',
                addressRegion: agent.region || '',
                addressCountry: 'FR',
            },
            ...(agent.avg_rating > 0 && agent.total_reviews > 0
                ? {
                    aggregateRating: {
                        '@type': 'AggregateRating',
                        ratingValue: agent.avg_rating,
                        reviewCount: agent.total_reviews,
                        bestRating: 5,
                        worstRating: 1,
                    },
                }
                : {}),
        };

        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.dataset.scalimmoSeo = 'agent';
        script.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(script);

        return () => {
            try { document.head.removeChild(script); } catch (_) { /* noop */ }
        };
    }, [agent, id]);

    useEffect(() => {
        if (selectedDate) {
            fetchAvailabilities(format(selectedDate, 'yyyy-MM-dd'));
        }
    }, [selectedDate]);

    const fetchAgent = async () => {
        try {
            const response = await axios.get(`${API}/agents/${id}`);
            setAgent(response.data);
        } catch (error) {
            toast.error('Agent non trouvé');
            navigate('/agents');
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailabilities = async (date) => {
        try {
            const response = await axios.get(`${API}/availabilities/agent/${id}?date=${date}`);
            setAvailabilities(response.data);
        } catch (error) {
            console.error('Error fetching availabilities:', error);
        }
    };

    const fetchReviews = async () => {
        try {
            const response = await axios.get(`${API}/reviews/agent/${id}`);
            setReviews(response.data);
        } catch (error) {
            console.error('Error fetching reviews:', error);
        }
    };

    const handleBooking = async () => {
        if (!user) {
            toast.error('Veuillez vous connecter pour réserver');
            navigate('/login');
            return;
        }

        if (user.user_type !== 'seller') {
            toast.error('Seuls les vendeurs peuvent réserver');
            return;
        }

        setBookingLoading(true);
        try {
            await axios.post(
                `${API}/appointments`,
                {
                    agent_id: id,
                    availability_id: selectedSlot.id,
                    notes: bookingNotes,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Rendez-vous réservé avec succès !');
            setShowBookingModal(false);
            setSelectedSlot(null);
            setBookingNotes('');
            fetchAvailabilities(format(selectedDate, 'yyyy-MM-dd'));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
        } finally {
            setBookingLoading(false);
        }
    };

    const defaultPhoto = agent
        ? `https://ui-avatars.com/api/?name=${agent.first_name}+${agent.last_name}&background=0F172A&color=fff&size=200`
        : '';

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-scalimmo-navy" />
            </div>
        );
    }

    if (!agent) return null;

    // SEO meta values
    const fullName = `${agent.first_name || ''} ${agent.last_name || ''}`.trim();
    const seoTitle = `${fullName} — Agent immobilier${agent.agency_name ? ` · ${agent.agency_name}` : ''} · Scalimmo`;
    const seoDescription = (agent.bio?.trim()?.slice(0, 160)) || `Agent immobilier${agent.agency_name ? ` chez ${agent.agency_name}` : ''}${agent.city ? ` à ${agent.city}` : ''}. Prenez rendez-vous en ligne sur Scalimmo.`;
    const profilePic = agent.photo_url
        ? (agent.photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${agent.photo_url}` : agent.photo_url)
        : `${process.env.REACT_APP_BACKEND_URL}/icons/icon-512.png`;
    const profileUrl = typeof window !== 'undefined' ? `${window.location.origin}/agents/${id}` : '';

    return (
        <div className="min-h-screen pt-24 pb-16" data-testid="agent-profile-page">
            <Helmet
                title={seoTitle}
                meta={[
                    { name: 'description', content: seoDescription },
                    { property: 'og:type', content: 'profile' },
                    { property: 'og:site_name', content: 'Scalimmo' },
                    { property: 'og:title', content: seoTitle },
                    { property: 'og:description', content: seoDescription },
                    { property: 'og:url', content: profileUrl },
                    { property: 'og:image', content: profilePic },
                    { property: 'og:locale', content: 'fr_FR' },
                    { property: 'profile:first_name', content: agent.first_name || '' },
                    { property: 'profile:last_name', content: agent.last_name || '' },
                    { name: 'twitter:card', content: 'summary_large_image' },
                    { name: 'twitter:title', content: seoTitle },
                    { name: 'twitter:description', content: seoDescription },
                    { name: 'twitter:image', content: profilePic },
                ]}
                link={[
                    { rel: 'canonical', href: profileUrl },
                ]}
            />
            <div className="container-scalimmo">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/agents')}
                    className="inline-flex items-center gap-2 text-muted-foreground hover:text-scalimmo-navy mb-8 transition-colors"
                    data-testid="back-button"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Retour aux agents
                </button>

                <div className="grid lg:grid-cols-3 gap-10">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Profile Header */}
                        <div className={`bg-white rounded-xl p-6 md:p-8 ${agent.is_top_performer ? 'elite-card' : 'border border-border/60 shadow-soft'}`}>
                            <div className="flex flex-col md:flex-row gap-6">
                                {/* Photo */}
                                <div className="relative">
                                    <img
                                        src={agent.photo_url || defaultPhoto}
                                        alt={`${agent.first_name} ${agent.last_name}`}
                                        className="w-32 h-32 rounded-xl object-cover"
                                    />
                                    {agent.is_top_performer && (
                                        <div className="absolute -top-2 -right-2">
                                            <span className="elite-badge badge-elite text-[10px] py-1">
                                                <Award className="w-3 h-3" />
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Info */}
                                <div className="flex-1">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h1 className="font-heading text-3xl font-semibold text-scalimmo-navy">
                                                {agent.first_name} {agent.last_name}
                                            </h1>
                                            {agent.agency_name && (
                                                <p className="text-muted-foreground mt-1">{agent.agency_name}</p>
                                            )}
                                        </div>
                                        {agent.is_top_performer && (
                                            <span className="elite-badge hidden md:inline-flex">
                                                <Award className="w-4 h-4" />
                                                Top Performer
                                            </span>
                                        )}
                                    </div>

                                    {/* Stats */}
                                    <div className="flex flex-wrap items-center gap-4 mt-4">
                                        <div className="flex items-center gap-1">
                                            <StarRating rating={agent.avg_rating || 0} readonly size="sm" />
                                            <span className="font-semibold text-scalimmo-navy ml-1">
                                                {(agent.avg_rating || 0).toFixed(1)}
                                            </span>
                                            <span className="text-muted-foreground text-sm">
                                                ({agent.total_reviews || 0} avis)
                                            </span>
                                        </div>
                                        {agent.city && (
                                            <div className="flex items-center gap-1.5 text-muted-foreground">
                                                <MapPin className="w-4 h-4" />
                                                {agent.city}
                                            </div>
                                        )}
                                    </div>

                                    {/* Contact */}
                                    <div className="flex flex-wrap gap-4 mt-4">
                                        {agent.email && (
                                            <a
                                                href={`mailto:${agent.email}`}
                                                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-scalimmo-navy"
                                            >
                                                <Mail className="w-4 h-4" />
                                                {agent.email}
                                            </a>
                                        )}
                                        {agent.phone && (
                                            <a
                                                href={`tel:${agent.phone}`}
                                                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-scalimmo-navy"
                                            >
                                                <Phone className="w-4 h-4" />
                                                {agent.phone}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Bio */}
                            {agent.bio && (
                                <div className="mt-6 pt-6 border-t border-border/60">
                                    <h3 className="font-heading text-lg font-semibold text-scalimmo-navy mb-2">
                                        Présentation
                                    </h3>
                                    <p className="text-muted-foreground leading-relaxed">{agent.bio}</p>
                                </div>
                            )}

                            {/* Specializations & Commission */}
                            <div className="flex flex-wrap gap-6 mt-6 pt-6 border-t border-border/60">
                                {agent.specializations && agent.specializations.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-medium text-muted-foreground mb-2">Spécialités</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {agent.specializations.map((spec, idx) => (
                                                <Badge key={idx} variant="secondary">
                                                    {spec}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {agent.commission_rate && (
                                    <div>
                                        <h4 className="text-sm font-medium text-muted-foreground mb-2">Honoraires</h4>
                                        <p className="text-2xl font-heading font-semibold text-scalimmo-navy">
                                            {agent.commission_rate}%
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Featured Properties - Top 3 */}
                        <FeaturedProperties agentId={id} />

                        {/* Reviews */}
                        <div className="bg-white rounded-xl border border-border/60 p-6 md:p-8 shadow-soft">
                            <h2 className="font-heading text-2xl font-semibold text-scalimmo-navy mb-6">
                                Avis clients
                                <span className="text-sm font-normal text-muted-foreground ml-2">
                                    ({reviews.length})
                                </span>
                            </h2>

                            {reviews.length === 0 ? (
                                <div className="text-center py-8">
                                    <Star className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                                    <p className="text-muted-foreground">Aucun avis pour le moment</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {reviews.map((review) => (
                                        <div
                                            key={review.id}
                                            className="border-b border-border/60 pb-6 last:border-0 last:pb-0"
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-scalimmo-navy/10 flex items-center justify-center">
                                                        <User className="w-5 h-5 text-scalimmo-navy" />
                                                    </div>
                                                    <span className="font-medium text-scalimmo-navy">
                                                        {review.seller_name || 'Client vérifié'}
                                                    </span>
                                                </div>
                                                <StarRating rating={review.rating} readonly size="sm" />
                                            </div>
                                            {review.comment && (
                                                <p className="text-muted-foreground mt-2">{review.comment}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar - Booking */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl border border-border/60 p-6 shadow-soft sticky top-28">
                            <h2 className="font-heading text-xl font-semibold text-scalimmo-navy mb-4">
                                <CalendarIcon className="w-5 h-5 inline mr-2" />
                                Prendre rendez-vous
                            </h2>

                            {/* Calendar */}
                            <Calendar
                                mode="single"
                                selected={selectedDate}
                                onSelect={setSelectedDate}
                                disabled={(date) => date < new Date()}
                                className="rounded-md border mb-4"
                                locale={fr}
                            />

                            {/* Time Slots */}
                            {selectedDate && (
                                <div className="mt-4">
                                    <h3 className="text-sm font-medium text-muted-foreground mb-3">
                                        Créneaux disponibles le {format(selectedDate, 'dd MMMM', { locale: fr })}
                                    </h3>

                                    {availabilities.length === 0 ? (
                                        <p className="text-sm text-muted-foreground text-center py-4">
                                            Aucun créneau disponible
                                        </p>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-2">
                                            {availabilities.map((slot) => (
                                                <button
                                                    key={slot.id}
                                                    onClick={() => {
                                                        setSelectedSlot(slot);
                                                        setShowBookingModal(true);
                                                    }}
                                                    className={`time-slot flex items-center justify-center gap-1.5 py-3 px-4 rounded-lg border text-sm font-medium transition-colors ${
                                                        selectedSlot?.id === slot.id
                                                            ? 'selected'
                                                            : 'border-border hover:border-scalimmo-navy'
                                                    }`}
                                                    data-testid={`slot-${slot.id}`}
                                                >
                                                    <Clock className="w-4 h-4" />
                                                    {slot.start_time}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {!selectedDate && (
                                <p className="text-sm text-muted-foreground text-center">
                                    Sélectionnez une date pour voir les disponibilités
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Booking Modal */}
            <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Confirmer le rendez-vous</DialogTitle>
                        <DialogDescription>
                            Avec {agent.first_name} {agent.last_name}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="bg-muted rounded-lg p-4">
                            <div className="flex items-center gap-2 text-scalimmo-navy font-medium">
                                <CalendarIcon className="w-4 h-4" />
                                {selectedDate && format(selectedDate, 'EEEE dd MMMM yyyy', { locale: fr })}
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground mt-1">
                                <Clock className="w-4 h-4" />
                                {selectedSlot?.start_time} - {selectedSlot?.end_time}
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-scalimmo-navy mb-2 block">
                                Notes (optionnel)
                            </label>
                            <Textarea
                                placeholder="Décrivez brièvement votre projet..."
                                value={bookingNotes}
                                onChange={(e) => setBookingNotes(e.target.value)}
                                rows={3}
                                data-testid="booking-notes"
                            />
                        </div>

                        <Button
                            className="w-full btn-primary"
                            onClick={handleBooking}
                            disabled={bookingLoading}
                            data-testid="confirm-booking"
                        >
                            {bookingLoading ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Réservation...
                                </span>
                            ) : (
                                <>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Confirmer le rendez-vous
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AgentProfilePage;
